import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';

/**
 * useAudioControls Hook
 * 
 * Manages audio-related state including mute/unmute, push-to-talk,
 * microphone volume, and audio mode. Integrates with useWebRTC hook
 * for audio track control. Extracted from ChatApp component.
 */
export const useAudioControls = ({ 
  audioMode: initialAudioMode = "pushToTalk",
  conversationStarted = false,
  localStreamRef,
  setWebRTCMuted,
  setIsTalking: setWebRTCIsTalking,
  micVolume = 0
}) => {
  // Audio state
  const [muted, setMuted] = useState(true); // Default to muted for simplified audio control
  const [isTalking, setIsTalking] = useState(false);
  const [audioMode, setAudioMode] = useState(initialAudioMode);
  const setWebRTCIsTalkingRef = useRef(setWebRTCIsTalking);

  useEffect(() => {
    setWebRTCIsTalkingRef.current = setWebRTCIsTalking;
  }, [setWebRTCIsTalking]);

  const getAudioTrack = useCallback(
    () => localStreamRef?.current?.getAudioTracks?.()[0] || null,
    [localStreamRef]
  );

  const setExternalTalking = useCallback((talking) => {
    setWebRTCIsTalkingRef.current?.(talking);
  }, []);

  const stopTransmitting = useCallback(() => {
    const audioTrack = getAudioTrack();
    if (audioTrack) {
      audioTrack.enabled = false;
    }

    setMuted(true);
    setIsTalking(false);
    setExternalTalking(false);
  }, [getAudioTrack, setExternalTalking]);

  // Toggle mute/unmute functionality
  const toggleMute = () => {
    // console.log("toggleMute called, current muted state:", muted);
    if (audioMode === "mute") {
      const newMuted = !muted;
      // console.log("Setting new muted state:", newMuted);

      // Use the setWebRTCMuted function from useWebRTC
      const success = setWebRTCMuted ? setWebRTCMuted(newMuted) : false;

      if (success) {
        // Update React state only if the audio track was successfully updated
        setMuted(newMuted);
        setIsTalking(!newMuted);
        if (setWebRTCIsTalking) {
          setWebRTCIsTalking(!newMuted);
        }
        // console.log("Audio track enabled state updated successfully");
      } else {
        console.warn("Could not change mute state - no audio track available");
        toast.warn("Microphone not available. Please check your device settings.");
      }
    } else {
      // console.log("audioMode is not mute:", { audioMode });
    }
  };

  // Dedicated handler for mute button click
  const handleMuteButtonClick = (e) => {
    // console.log("Mute button clicked");
    e.preventDefault();
    e.stopPropagation();

    const newMuted = !muted;
    // console.log("Setting new muted state:", newMuted);

    // Use the setWebRTCMuted function from useWebRTC
    const success = setWebRTCMuted ? setWebRTCMuted(newMuted) : false;

    if (success) {
      // Update React state only if the audio track was successfully updated
      setMuted(newMuted);
      setIsTalking(!newMuted);
      if (setWebRTCIsTalking) {
        setWebRTCIsTalking(!newMuted);
      }
      // console.log("Audio track enabled state updated successfully");
    } else {
      console.warn("Could not change mute state - no audio track available");
      toast.warn("Microphone not available. Please check your device settings.");
    }
  };

  // Push-to-talk keyboard handler
  const handlePushToTalk = useCallback((event) => {
    if (audioMode === "pushToTalk" && conversationStarted) {
      const audioTrack = getAudioTrack();
      if (audioTrack) {
        if (event.type === "keydown" && event.key === " ") {
          event.preventDefault?.();
          audioTrack.enabled = true;
          setIsTalking(true);
          setExternalTalking(true);
        } else if (event.type === "keyup" && event.key === " ") {
          event.preventDefault?.();
          audioTrack.enabled = false;
          setIsTalking(false);
          setExternalTalking(false);
        }
      }
    }
  }, [audioMode, conversationStarted, getAudioTrack, setExternalTalking]);

  // Push-to-talk start handler (mouse/touch)
  const handlePushToTalkStart = useCallback((e) => {
    e?.preventDefault?.();
    if (audioMode === "pushToTalk") {
      const audioTrack = getAudioTrack();
      if (audioTrack) {
        audioTrack.enabled = true;
        setMuted(false);
        setIsTalking(true);
        setExternalTalking(true);
      }
    }
  }, [audioMode, getAudioTrack, setExternalTalking]);

  // Push-to-talk end handler (mouse/touch)
  const handlePushToTalkEnd = useCallback((e) => {
    e?.preventDefault?.();
    if (audioMode === "pushToTalk") {
      const audioTrack = getAudioTrack();
      if (audioTrack) {
        audioTrack.enabled = false;
      }

      setMuted(true);
      setIsTalking(false);
      setExternalTalking(false);
    }
  }, [audioMode, getAudioTrack, setExternalTalking]);

  // Set up keyboard event listeners for push-to-talk
  useEffect(() => {
    if (audioMode === "pushToTalk" && conversationStarted) {
      window.addEventListener("keydown", handlePushToTalk);
      window.addEventListener("keyup", handlePushToTalk);
      return () => {
        window.removeEventListener("keydown", handlePushToTalk);
        window.removeEventListener("keyup", handlePushToTalk);
      };
    }
  }, [audioMode, conversationStarted, handlePushToTalk]);

  useEffect(() => {
    if (initialAudioMode === "mute" || initialAudioMode === "pushToTalk") {
      setAudioMode(initialAudioMode);
    }
  }, [initialAudioMode]);

  // Handle audio mode changes
  useEffect(() => {
    if (audioMode === "pushToTalk" || audioMode === "mute") {
      stopTransmitting();
    }
  }, [audioMode, stopTransmitting]);

  // Update audio mode
  const updateAudioMode = (newMode) => {
    if (newMode !== "mute" && newMode !== "pushToTalk") {
      return;
    }

    setAudioMode(newMode);
    stopTransmitting();
  };

  return {
    // State
    muted,
    isTalking,
    micVolume,
    audioMode,
    
    // Actions
    toggleMute,
    handleMuteButtonClick,
    handlePushToTalkStart,
    handlePushToTalkEnd,
    setAudioMode: updateAudioMode,
    
    // Internal handlers (for keyboard events)
    handlePushToTalk
  };
};

export default useAudioControls;
